/**
 * Anlaesse der arbeitsmedizinischen Vorsorge nach dem Anhang der ArbMedVV
 * (SiFa-Modul GBU, Vorsorgeanlass je Gefaehrdung).
 *
 * ─── Fassung 1, vom Betreiber fachlich zu pruefen ────────────────────────
 * Bevor die Fassung aktiv wird, prueft der Betreiber jeden Eintrag gegen den
 * geltenden Verordnungstext (docs/sifa-gbu-plan.md §5).
 *
 * ─── Was hier steht und was nicht ────────────────────────────────────────
 * Nur Anlaesse aus dem ANHANG der ArbMedVV (Teil 1 Gefahrstoffe, Teil 2
 * Biostoffe, Teil 3 physikalische Einwirkungen, Teil 4 Sonstiges), der Inhalt
 * aus dem Verordnungstext und nicht aus dem Muster des Betreibers. Bewusst
 * NICHT hier:
 *   • DGUV-Grundsaetze („G 20", „G 25" …) — das sind Untersuchungsleitfaeden,
 *     keine Anlaesse.
 *   • Eignungsuntersuchungen (z. B. Fahr-, Steuer- und Ueberwachungs-
 *     taetigkeiten, „G 25") — sie sind keine Vorsorge nach ArbMedVV. Das
 *     Muster fuehrte G 25 als Pflichtvorsorge nach Anhang Teil 4; das ist der
 *     Fehler, den dieser Katalog verhindern soll (Plan §3).
 *   • Eintraege, die fuer die Kunden des Betreibers fern liegen oder deren
 *     Voreinstellung unsicher war (Gesundheitsdienst, Labor, Tierhaltung,
 *     Getreide- und Mehlstaub, Naturgummilatex, Hochtemperaturwollen,
 *     Hartholzstaub). Lieber weglassen als raten — sie lassen sich mit einer
 *     neuen Fassung ergaenzen.
 *
 * ─── Die Felder ──────────────────────────────────────────────────────────
 * `fundstelle` ist ein EIGENES Feld und nie Teil eines Regelwerksfelds:
 * `bereinigeAngabe` schneidet dort „Anhang", „Teil" und „Abs." ab (Plan
 * §6.3). Der Code steht in `sifa_gbu_vorsorge.anlass_code`.
 *
 * `standardArt` ist die Vorsorgeart im Regelfall des Anlasses — das, was der
 * Editor vorbelegt, solange kein Messwert vorliegt. Bei Anlaessen mit
 * Schwelle ist das 'angebot': der Anlass entsteht erst oberhalb der unteren
 * Schwelle, Pflicht wird er erst oberhalb der oberen. „Pflicht fuer alle"
 * (Muster, Laerm) ist genau der Fehler aus Plan §3.
 *
 * `regel` gibt es NUR, wo die Verordnung die Schwelle in der Form
 * „Angebot, wenn X ueberschritten — Pflicht, wenn Y erreicht oder
 * ueberschritten" nennt (Laerm, Vibration, Feuchtarbeit):
 *   messwert ≥ pflichtAb       → 'pflicht'
 *   messwert > angebotUeber    → 'angebot'
 *   darunter                   → 'wunsch' (§ 5a ArbMedVV)
 * Schwellen der Form „Pflicht bei Ueberschreitung, Angebot bei Einhaltung"
 * (Schweissrauch, Blei, Isocyanate) passen an der Grenze nicht in diese
 * Form — dort steht der Wert nur in der Beschreibung, die Regel ist null.
 * Ebenso bei Kaelte (die Schwelle zaehlt nach unten) und UV-Strahlung
 * („eine Stunde oder mehr" ist kein „ueber").
 *
 * ─── Quelle ──────────────────────────────────────────────────────────────
 * Verordnung zur arbeitsmedizinischen Vorsorge (ArbMedVV), Anhang Teil 1–4,
 * Stand des Textes bei gesetze-im-internet.de im Oktober 2026 ·
 * LaermVibrationsArbSchV §§ 6, 9 (dieselben Werte).
 */

import type { VorsorgeArt } from '../gbuTypes';

/** Fassung des Katalogs. Bei jeder inhaltlichen Aenderung erhoehen. */
export const ARBMEDVV_FASSUNG = 1;

export interface VorsorgeRegel {
  /** Einheit des Messwerts, z. B. 'dB(A)', 'm/s²', 'h/Tag'. */
  einheit: string;
  /** Angebotsvorsorge, wenn der Messwert diesen Wert UEBERSCHREITET (>). */
  angebotUeber: number | null;
  /** Pflichtvorsorge, wenn der Messwert diesen Wert ERREICHT oder ueberschreitet (≥). */
  pflichtAb: number | null;
}

export interface ArbmedvvAnlass {
  code: string;
  label: string;
  /** Kurzbeschreibung mit den Bedingungen aus dem Verordnungstext. */
  beschreibung: string;
  /** Teil des Anhangs (1 Gefahrstoffe, 2 Biostoffe, 3 physikalisch, 4 sonstige). */
  teil: 1 | 2 | 3 | 4;
  /** Fundstelle im Anhang — eigenes Feld, nie in einem Regelwerksfeld. */
  fundstelle: string;
  /** Nur wo die Verordnung Schwellen nennt; sonst null. */
  regel: VorsorgeRegel | null;
  /** Vorsorgeart im Regelfall des Anlasses (Vorbelegung ohne Messwert). */
  standardArt: VorsorgeArt;
}

export const ARBMEDVV_ANLAESSE: ReadonlyArray<ArbmedvvAnlass> = [
  // ═══ Teil 1 — Taetigkeiten mit Gefahrstoffen ══════════════════════════════
  {
    code: 'feuchtarbeit',
    label: 'Feuchtarbeit',
    beschreibung: 'Regelmäßig mehr als zwei Stunden je Tag: Angebot; vier Stunden oder mehr: Pflicht. Dazu zählt das Tragen flüssigkeitsdichter Handschuhe (TRGS 401).',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 2 Buchst. a (Pflicht); Abs. 2 Nr. 2 Buchst. e (Angebot)',
    regel: { einheit: 'h/Tag', angebotUeber: 2, pflichtAb: 4 },
    standardArt: 'angebot',
  },
  {
    code: 'schweissrauch',
    label: 'Schweißen und Trennen von Metallen',
    beschreibung: 'Pflicht bei Überschreitung einer Luftkonzentration von 3 mg/m³ Schweißrauch, Angebot bei Einhaltung.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 2 Buchst. b (Pflicht); Abs. 2 Nr. 2 Buchst. f (Angebot)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'isocyanate',
    label: 'Isocyanate (z. B. PU-Schaum, PU-Beschichtungen)',
    beschreibung: 'Pflicht, wenn regelmäßiger Hautkontakt nicht ausgeschlossen werden kann oder die Luftkonzentration 0,05 mg/m³ überschritten wird; sonst Angebot, wenn Hautkontakt nicht ausgeschlossen werden kann oder die Konzentration eingehalten wird.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 2 Buchst. d (Pflicht); Abs. 2 Nr. 2 Buchst. h (Angebot)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'epoxidharze',
    label: 'Unausgehärtete Epoxidharze',
    beschreibung: 'Pflicht bei Hautgefährdung oder Einatmen von Bestandteilen unausgehärteter Epoxidharze, insbesondere beim Versprühen.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 2 Buchst. g',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'blei',
    label: 'Blei und anorganische Bleiverbindungen',
    beschreibung: 'Pflicht bei Überschreitung von 15 µg/m³ Luft, Angebot bei Einhaltung; nachgehende Vorsorge. Z. B. Entfernen bleihaltiger Altanstriche.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 2 Buchst. h (Pflicht); Abs. 2 Nr. 2 Buchst. i (Angebot); Abs. 3 Nr. 2 (nachgehend)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'a_staub',
    label: 'Alveolengängiger Staub (A-Staub)',
    beschreibung: 'Pflicht, wenn der Arbeitsplatzgrenzwert nicht eingehalten wird; sonst Angebot, wenn eine Exposition nicht ausgeschlossen werden kann.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 1 (Pflicht); Abs. 2 Nr. 1 (Angebot)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'e_staub',
    label: 'Einatembarer Staub (E-Staub)',
    beschreibung: 'Pflicht, wenn der Arbeitsplatzgrenzwert nicht eingehalten wird; sonst Angebot, wenn eine Exposition nicht ausgeschlossen werden kann.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 1 (Pflicht); Abs. 2 Nr. 1 (Angebot)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'quarzstaub',
    label: 'Silikogener Staub (Quarz, Cristobalit)',
    beschreibung: 'Tätigkeiten mit alveolengängigem Quarz- und Cristobalitstaub gelten als krebserzeugend (TRGS 906): Pflicht, wenn eine wiederholte Exposition nicht ausgeschlossen werden kann, z. B. beim Schleifen, Fräsen, Bohren und Stemmen von Beton und Mauerwerk; nachgehende Vorsorge.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 1 (Pflicht); Abs. 3 Nr. 1 (nachgehend)',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'asbest',
    label: 'Asbest',
    beschreibung: 'Krebserzeugend Kategorie 1A: Pflicht, wenn eine wiederholte Exposition nicht ausgeschlossen werden kann (Tätigkeiten nach TRGS 519); nachgehende Vorsorge.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 1 (Pflicht); Abs. 3 Nr. 1 (nachgehend)',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'pak',
    label: 'PAK (Pyrolyseprodukte, z. B. teerhaltige Baustoffe)',
    beschreibung: 'Polycyclische aromatische Kohlenwasserstoffe aus Pyrolyseprodukten organischen Materials, krebserzeugend: Pflicht, wenn eine wiederholte Exposition nicht ausgeschlossen werden kann; nachgehende Vorsorge.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 1 Nr. 1 (Pflicht); Abs. 3 Nr. 1 (nachgehend)',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'kmr_sonstige',
    label: 'Sonstige krebserzeugende oder keimzellmutagene Stoffe (Kat. 1A/1B)',
    beschreibung: 'Stoffe, Gemische, Tätigkeiten oder Verfahren der Kategorie 1A/1B, die nicht in Abs. 1 Nr. 1 genannt sind, bei nicht ausgeschlossener wiederholter Exposition: Angebot; nachgehende Vorsorge.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 2 Nr. 2 Buchst. d (Angebot); Abs. 3 Nr. 1 (nachgehend)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'loesemittel',
    label: 'Lösemittel (z. B. Toluol, Xylol, Ethanol)',
    beschreibung: 'n-Hexan, n-Heptan, 2-Butanon, 2-Hexanon, Methanol, Ethanol, 2-Methoxyethanol, Benzol, Toluol, Xylol, Styrol, Dichlormethan, 1,1,1-Trichlorethan, Trichlorethen, Tetrachlorethen: Angebot. Für Benzol, Methanol, Styrol, Tetrachlorethen, Toluol, Trichlorethen und Xylol gilt zusätzlich Abs. 1 Nr. 1 (Pflicht, wenn der Arbeitsplatzgrenzwert nicht eingehalten wird oder bei Hautkontakt mit einem hautresorptiven Stoff).',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 2 Nr. 2 Buchst. c (Angebot); Abs. 1 Nr. 1 (Pflicht)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'sensibilisierende_stoffe',
    label: 'Atemwegs- oder hautsensibilisierende Stoffe',
    beschreibung: 'Sonstige sensibilisierend wirkende Stoffe (z. B. H317, H334), für die nach Abs. 1 keine Pflichtvorsorge vorgesehen ist: Angebot.',
    teil: 1,
    fundstelle: 'ArbMedVV Anhang Teil 1 Abs. 2 Nr. 2 Buchst. k',
    regel: null,
    standardArt: 'angebot',
  },

  // ═══ Teil 2 — Taetigkeiten mit biologischen Arbeitsstoffen ════════════════
  {
    code: 'bio_abwasser',
    label: 'Abwasser und Kanalisation (Hepatitis A)',
    beschreibung: 'Tätigkeiten in Kläranlagen oder in der Kanalisation mit regelmäßigem Kontakt zu fäkalienhaltigem Abwasser oder fäkalienkontaminierten Gegenständen; Erreger Hepatitis-A-Virus.',
    teil: 2,
    fundstelle: 'ArbMedVV Anhang Teil 2 Abs. 1 Nr. 3 Buchst. i',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'bio_natur',
    label: 'Freiflächen, Wald, Parks (Borrelien, FSME)',
    beschreibung: 'Regelmäßige Tätigkeiten in niederer Vegetation oder mit direktem Kontakt zu frei lebenden Tieren auf Freiflächen, in Wäldern, Parks und Gartenanlagen, Tiergärten und Zoos; Borrelia burgdorferi, in Endemiegebieten FSME-Virus.',
    teil: 2,
    fundstelle: 'ArbMedVV Anhang Teil 2 Abs. 1 Nr. 3 Buchst. m',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'bio_schutzstufe_3',
    label: 'Biostoffe Risikogruppe 3 / Schutzstufe 3',
    beschreibung: 'Gezielte Tätigkeiten mit Biostoffen der Risikogruppe 3 und nicht gezielte Tätigkeiten der Schutzstufe 3 oder mit vergleichbarer Gefährdung, soweit keine Pflichtvorsorge vorgesehen ist.',
    teil: 2,
    fundstelle: 'ArbMedVV Anhang Teil 2 Abs. 2 Nr. 1 Buchst. a',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'bio_schutzstufe_2',
    label: 'Biostoffe Risikogruppe 2 / Schutzstufe 2',
    beschreibung: 'Gezielte Tätigkeiten mit Biostoffen der Risikogruppe 2 und nicht gezielte Tätigkeiten der Schutzstufe 2 oder mit vergleichbarer Gefährdung — außer wenn nach der Gefährdungsbeurteilung und den Schutzmaßnahmen keine Infektionsgefährdung besteht.',
    teil: 2,
    fundstelle: 'ArbMedVV Anhang Teil 2 Abs. 2 Nr. 1 Buchst. b',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'bio_sensibilisierend',
    label: 'Sensibilisierend oder toxisch wirkende Biostoffe (z. B. Schimmelpilze)',
    beschreibung: 'Exposition gegenüber sensibilisierend oder toxisch wirkenden Biostoffen, für die keine Pflichtvorsorge vorgesehen ist, z. B. bei der Schimmelsanierung.',
    teil: 2,
    fundstelle: 'ArbMedVV Anhang Teil 2 Abs. 2 Nr. 1 Buchst. c',
    regel: null,
    standardArt: 'angebot',
  },

  // ═══ Teil 3 — Taetigkeiten mit physikalischen Einwirkungen ════════════════
  {
    code: 'hitze',
    label: 'Extreme Hitzebelastung',
    beschreibung: 'Tätigkeiten mit extremer Hitzebelastung, die zu einer besonderen Gefährdung führen können.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 1',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'kaelte',
    label: 'Extreme Kältebelastung (−25 °C und kälter)',
    beschreibung: 'Tätigkeiten mit extremer Kältebelastung bei −25 °C und kälter.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 2',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'laerm',
    label: 'Lärm',
    beschreibung: 'Tages-Lärmexpositionspegel: Angebot über 80 dB(A), Pflicht ab 85 dB(A). Spitzenschalldruckpegel: Angebot über 135 dB(C), Pflicht ab 137 dB(C). Die Dämmwirkung des Gehörschutzes wird nicht angerechnet.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 3 (Pflicht); Abs. 2 Nr. 1 (Angebot)',
    regel: { einheit: 'dB(A)', angebotUeber: 80, pflichtAb: 85 },
    standardArt: 'angebot',
  },
  {
    code: 'vibration_hand_arm',
    label: 'Hand-Arm-Vibration',
    beschreibung: 'Tages-Vibrationsexpositionswert A(8): Angebot über 2,5 m/s², Pflicht ab 5 m/s².',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 4 Buchst. a (Pflicht); Abs. 2 Nr. 2 Buchst. a (Angebot)',
    regel: { einheit: 'm/s²', angebotUeber: 2.5, pflichtAb: 5 },
    standardArt: 'angebot',
  },
  {
    // Zwei Codes fuer die Ganzkoerper-Vibration, weil die Pflichtschwelle je
    // Richtung verschieden ist (x/y 1,15, z 0,8 m/s²) — eine Regel je Code.
    code: 'vibration_ganzkoerper_xy',
    label: 'Ganzkörper-Vibration (x-/y-Richtung)',
    beschreibung: 'Tages-Vibrationsexpositionswert A(8) in x- oder y-Richtung: Angebot über 0,5 m/s², Pflicht ab 1,15 m/s².',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 4 Buchst. b (Pflicht); Abs. 2 Nr. 2 Buchst. b (Angebot)',
    regel: { einheit: 'm/s²', angebotUeber: 0.5, pflichtAb: 1.15 },
    standardArt: 'angebot',
  },
  {
    code: 'vibration_ganzkoerper_z',
    label: 'Ganzkörper-Vibration (z-Richtung)',
    beschreibung: 'Tages-Vibrationsexpositionswert A(8) in z-Richtung: Angebot über 0,5 m/s², Pflicht ab 0,8 m/s².',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 4 Buchst. b (Pflicht); Abs. 2 Nr. 2 Buchst. b (Angebot)',
    regel: { einheit: 'm/s²', angebotUeber: 0.5, pflichtAb: 0.8 },
    standardArt: 'angebot',
  },
  {
    code: 'taucherarbeiten',
    label: 'Taucherarbeiten',
    beschreibung: 'Tätigkeiten unter Wasser mit Atemgasversorgung über ein Tauchgerät.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 5',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'optische_strahlung',
    label: 'Inkohärente künstliche optische Strahlung (z. B. Lichtbogenschweißen)',
    beschreibung: 'Pflicht, wenn die Expositionsgrenzwerte nach § 6 OStrV überschritten werden; Angebot, wenn sie überschritten werden können. Laserstrahlung fällt nicht darunter.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 1 Nr. 6 (Pflicht); Abs. 2 Nr. 3 (Angebot)',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'mse_lasten',
    label: 'Lastenhandhabung (Heben, Halten, Tragen, Ziehen, Schieben)',
    beschreibung: 'Wesentlich erhöhte körperliche Belastung mit Gesundheitsgefährdung für das Muskel-Skelett-System durch Lastenhandhabung.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 2 Nr. 4 Buchst. a',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'mse_repetitiv',
    label: 'Repetitive manuelle Tätigkeiten',
    beschreibung: 'Wesentlich erhöhte körperliche Belastung mit Gesundheitsgefährdung für das Muskel-Skelett-System durch repetitive manuelle Tätigkeiten.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 2 Nr. 4 Buchst. b',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'mse_zwangshaltung',
    label: 'Erzwungene Körperhaltungen (Knien, Rumpfbeugen/-drehen)',
    beschreibung: 'Wesentlich erhöhte körperliche Belastung durch Arbeiten im Knien, in langdauerndem Rumpfbeugen oder -drehen oder in vergleichbaren Zwangshaltungen.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 2 Nr. 4 Buchst. c',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'uv_natuerlich',
    label: 'Natürliche UV-Strahlung (Arbeiten im Freien)',
    beschreibung: 'Tätigkeiten im Freien mit intensiver Belastung durch natürliche UV-Strahlung von regelmäßig einer Stunde oder mehr je Tag.',
    teil: 3,
    fundstelle: 'ArbMedVV Anhang Teil 3 Abs. 2 Nr. 5',
    regel: null,
    standardArt: 'angebot',
  },

  // ═══ Teil 4 — Sonstige Taetigkeiten ════════════════════════════════════════
  {
    code: 'atemschutz_gruppe_1',
    label: 'Atemschutzgeräte Gruppe 1',
    beschreibung: 'Tätigkeiten, die das Tragen von Atemschutzgeräten der Gruppe 1 erfordern (Einteilung nach AMR 14.2).',
    teil: 4,
    fundstelle: 'ArbMedVV Anhang Teil 4 Abs. 2 Nr. 2',
    regel: null,
    standardArt: 'angebot',
  },
  {
    code: 'atemschutz_gruppe_2_3',
    label: 'Atemschutzgeräte Gruppe 2 und 3',
    beschreibung: 'Tätigkeiten, die das Tragen von Atemschutzgeräten der Gruppen 2 und 3 erfordern (Einteilung nach AMR 14.2).',
    teil: 4,
    fundstelle: 'ArbMedVV Anhang Teil 4 Abs. 1 Nr. 1',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'tropen',
    label: 'Tropen, Subtropen und belastende Auslandsaufenthalte',
    beschreibung: 'Tätigkeiten in Tropen, Subtropen und sonstige Auslandsaufenthalte mit besonderen klimatischen Belastungen und Infektionsgefährdungen; am Ende der Tätigkeit Angebot.',
    teil: 4,
    fundstelle: 'ArbMedVV Anhang Teil 4 Abs. 1 Nr. 2 (Pflicht); Abs. 2 Nr. 3 (Angebot am Ende)',
    regel: null,
    standardArt: 'pflicht',
  },
  {
    code: 'bildschirm',
    label: 'Tätigkeiten an Bildschirmgeräten',
    beschreibung: 'Angebot einer angemessenen Untersuchung der Augen und des Sehvermögens.',
    teil: 4,
    fundstelle: 'ArbMedVV Anhang Teil 4 Abs. 2 Nr. 1',
    regel: null,
    standardArt: 'angebot',
  },
];

const NACH_CODE = new Map<string, ArbmedvvAnlass>(ARBMEDVV_ANLAESSE.map((a) => [a.code, a]));

/** Ist `code` ein Anlass-Code des Katalogs? (Der KI-Leser verwirft alles andere.) */
export const istAnlassCode = (code: string | null | undefined): boolean =>
  typeof code === 'string' && NACH_CODE.has(code);

/** Katalogeintrag zum Code, sonst null. */
export const anlassEintrag = (code: string | null | undefined): ArbmedvvAnlass | null =>
  (typeof code === 'string' && NACH_CODE.get(code)) || null;

/**
 * Anzeigename zum Code. Ein unbekannter Code kommt unveraendert zurueck statt
 * leer — ein Anlass mit fremdem Code soll auffallen, nicht verschwinden.
 */
export const anlassLabel = (code: string | null | undefined): string =>
  anlassEintrag(code)?.label ?? (code ?? '');
